import { CancelButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { RangeDateInput, TextInput } from '@core/components/Inputs';
import { useApiUtils } from '@core/util/apiUtils';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import { Button, Grid, Typography } from '@material-ui/core';
import SelectConta from 'app/components/Select/SelectConta';
import { contaApi } from 'app/services/api/contaApi';
import * as Actions from 'app/store/actions';
import { FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { FaArrowCircleUp } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import HistoricoRemessaTable from './HistoricoRemessaTable';
import { HistoricoRemessaActions } from './store';
import { HistoricoRemessaProvider, useHistoricoRemessa } from './store/historicoRemessaContext';
import { searchHistoricoRemessaStyle } from './styles';

const onlyNumberRegex = /^\d*$/;

const HistoricoSearchHeader = ({ contaInit, setarConta }) => {
  const [dataVencimentoInicio, setDataVencimentoInicio] = useState();
  const [dataVencimentoFim, setDataVencimentoFim] = useState();
  const [dataArquivoInicio, setDataArquivoInicio] = useState();
  const [dataArquivoFim, setDataArquivoFim] = useState();
  const [nossoNumero, setNossoNumero] = useState();

  const [, dispatch] = useHistoricoRemessa();

  const styles = searchHistoricoRemessaStyle();

  const schema = yup.object().shape({
    conta: yup
      .object()
      .nullable()
      .required(mensagemCampoObrigatorio('a conta bancária')),
  });

  const handleBuscar = () => {
    setarConta(formik.values?.conta);
    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        HistoricoRemessaActions.carregarRegistros(dispatch, {
          dataVencimentoInicio: dataVencimentoInicio?.format('YYYY-MM-DD'),
          dataVencimentoFim: dataVencimentoFim?.format('YYYY-MM-DD'),
          contaId: formik.values?.conta?.id,
          nossoNumero,
          dataArquivoInicio: dataArquivoInicio?.format('YYYY-MM-DD'),
          dataArquivoFim: dataArquivoFim?.format('YYYY-MM-DD'),
        });
      }
    });
  };

  const formik = useFormik({
    initialValues: { conta: contaInit },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: schema,
    onSubmit: handleBuscar,
  });

  const _errors = {
    conta: (formik.submitCount > 0 || formik.touched.conta) && formik.errors.conta,
  };

  return (
    <FormikProvider value={formik}>
      <Grid container spacing={1} alignItems="center" className="mt-10">
        <Grid item xs={3}>
          <RangeDateInput
            id="periodo.dataVencimentoInicio"
            value={[dataVencimentoInicio, dataVencimentoFim]}
            label="Período do vencimento "
            onChange={(inicio, fim) => {
              setDataVencimentoInicio(inicio);
              setDataVencimentoFim(fim);
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <RangeDateInput
            id="periodo.arquivo"
            value={[dataArquivoInicio, dataArquivoFim]}
            label="Período do arquivo "
            onChange={(inicio, fim) => {
              setDataArquivoInicio(inicio);
              setDataArquivoFim(fim);
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <TextInput
            id="nossoNumero"
            label="Nosso número"
            fullWidth
            value={nossoNumero}
            onChange={(event) => {
              setNossoNumero(event.target.value);
            }}
            maxLength={20}
            pattern={onlyNumberRegex}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectConta
            id="conta"
            name="conta"
            label="Conta bancária"
            tiposConta={['CONTA_CORRENTE', 'CONTA_POUPANCA']}
            onChange={formik.handleChange}
            handleBlur={formik.handleBlur}
            value={formik.values?.conta || ''}
            touched={!!_errors.conta}
            error={_errors.conta}
            required
          />
        </Grid>
        <Grid item xs={1}>
          <Button className={styles.buttonSave} onClick={formik.handleSubmit}>
            <Typography>Buscar</Typography>
          </Button>
        </Grid>
      </Grid>
    </FormikProvider>
  );
};

const HistoricoRemessaModal = (props) => {
  const { openModal, onCloseModal } = props;
  const [contaInit, setContaInit] = useState();

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const setarConta = (conta) => setContaInit(conta);

  const fetchBancoInit = async () => {
    HistoricoRemessaActions.setCarregandoRegistros(true);
    try {
      const response = await contaApi.pesquisar({
        size: 100,
        sort: 'descricao',
        page: 0,
        q: null,
        tiposConta: ['CONTA_CORRENTE', 'CONTA_POUPANCA'],
        homologadas: false,
      });
      let contaPrincipal;
      if (response?.data.length > 0) {
        contaPrincipal = response.data.filter((banco) => !!banco.contaBancaria.principal).map((banco) => banco);
        if (!contaPrincipal || contaPrincipal.length < 1) {
          setContaInit(response.data[0]);
        } else {
          setContaInit(contaPrincipal[0]);
        }
      }
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      HistoricoRemessaActions.setCarregandoRegistros(false);
    }
  };

  useEffect(() => {
    fetchBancoInit();
    // Efeito de montagem: roda uma única vez. Incluir as dependências reexecutaria a carga
    // inicial a cada render (loop de chamadas à API).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SimpleDialog fullScreen open={openModal} onClose={onCloseModal} innerScroll>
      {openModal && (
        <HistoricoRemessaProvider>
          <FormDialogContent
            fullScreen
            header={{
              title: 'Histórico de remessa',
              icon: <FaArrowCircleUp />,
              toolbar: (
                <>
                  <CancelButton title="fechar" type="button" onClick={onCloseModal}>
                    Fechar
                  </CancelButton>
                </>
              ),
            }}
            content={
              <>
                <HistoricoSearchHeader contaInit={contaInit} setarConta={setarConta} />
                <HistoricoRemessaTable contaInit={contaInit} />
              </>
            }
          />
        </HistoricoRemessaProvider>
      )}
    </SimpleDialog>
  );
};

export default HistoricoRemessaModal;
